import { v4 as uuidv4 } from 'uuid';
import Errored from '../../core/Errored';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { globalStyles as styles } from '../../core/style/gstheme';
import TitledTextField from '../../core/TitledTextField';
import withGS from '../../core/withGShoc';
import { MAX_LABEL_LENGTH } from '../constants';
import { getAllLabelValues, debounce, makeCancelable } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';

class SuggestionPrefLabelField extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isErrored: false,
            isDuplicate: false,
            isLocalDuplicate: false,
            suggestedLabel: "",
            searchID: null
        };
        this.cancellable = null;
    }

    findConcept = async (value) => {
        try {
            let searchParts = [
                {
                    predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                    value: "http://www.w3.org/2004/02/skos/core#Concept"
                },
                {
                    predicate: "http://www.w3.org/2004/02/skos/core#inScheme",
                    value: this.props.schemeIRI
                },
                {
                    predicate: "http://www.w3.org/2004/02/skos/core#prefLabel",
                    value: value,
                    isExactMatch: false
                }
            ];
            const data = await this.doAutocomplete(
                {
                    graph: [this.props.graph],
                    search: searchParts,
                    language: this.props.lang,
                    entailment: this.props.optionForXL ? ["propertyChain"] : [],
                    predicates: [
                        "http://www.w3.org/2004/02/skos/core#prefLabel",
                        "http://www.w3.org/2004/02/skos/core#inScheme",
                        "https://grafsync.graphifi.com/schema/gs/iri",
                        "https://grafsync.graphifi.com/schema/gs/pendingDeletion"
                    ]
                }).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
            // Ignore 'old' searches
            if (data.uuid && data.uuid !== this.state.searchID) return;
            // It's a duplicate if there is an equivalent label in the in-context scheme
            let isDuplicate = data.results && data.results.length > 0 ?
                data.results.some(result => {
                    // We may get an edge case of mutiple prefLabels in a single language
                    if (Array.isArray(result.prefLabel[this.props.lang])) {
                        return result.prefLabel[this.props.lang].some(label =>
                            label.toLowerCase() === this.state.label.toLowerCase());
                    }
                    return result.prefLabel[this.props.lang].toLowerCase() === this.state.suggestedLabel.toLowerCase();
                })
                :
                false;
            // Some stuff isn't found by Lucene search so we check for exact match. Still not 100% foolproof but close
            if (!isDuplicate) {
                isDuplicate = await this.findExactConcept(value, this.props.lang);
            }
            this.setState({ isDeleted: false, isDuplicate, isLocalDuplicate: false, checked: true, checking: false });
            this.props.isValid(this.state.suggestedLabel, isDuplicate);
        }
        catch (e) {
            if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
        }
    };

    findExactConcept = async (value, lang) => {
        // Server side sorting will put the exact match first so this is nice and fast even on big searches
        let searchParts = [
            {
                predicate: "http://www.w3.org/2004/02/skos/core#prefLabel",
                value,
                isText: true,
                isExactMatch: true
            },
            {
                predicate: "http://www.w3.org/2004/02/skos/core#inScheme",
                value: this.props.schemeIRI
            },
            {
                predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                value: ["http://www.w3.org/2004/02/skos/core#Concept"]
            }
        ];
        const data = await this.doAutocomplete(
            {
                graph: [this.props.graph],
                search: searchParts,
                language: lang,
                searchMode: 'Compliant',
                entailment: this.props.optionForXL ? ["propertyChain"] : [],
                predicates: [
                    "http://www.w3.org/2004/02/skos/core#prefLabel"
                ]
            }).promise;
        const isDuplicate = data.results && data.results.length > 0;
        return isDuplicate;
    };

    handleChange = event => {
        const t = event.target;
        this.setState({ [t.name]: t.value.trimLeft(), checked: false });
        if (t.value.trimLeft() !== "") {
            // First we check local values on the concept
            const data = this.props.data;
            const lang = this.props.lang;
            const allLabels = getAllLabelValues(data, lang);
            let isDuplicate = allLabels.some(al => al.toLowerCase() === t.value.trimLeft().toLowerCase());
            if (!isDuplicate) {
                // If not a duplicate we check more widely
                this.props.isValid(t.value.trim(), false);
                this.setState({ isDuplicate, checking: true, suggestedLabel: t.value.trimLeft() }, debounce(this.findConcept, this, [t.value.trimLeft(), this.props.lang]));
            }
            else {
                if (this.hcTimeout) clearTimeout(this.hcTimeout);
                this.setState({ isDuplicate, isLocalDuplicate: true, suggestedLabel: t.value.trimLeft() });
                this.props.isValid(t.value.trim(), isDuplicate);
            }
        }
        else {
            // We set a search ID so all inflight searches are ignored for empty text
            const searchID = uuidv4();
            this.setState({ searchID, isDuplicate: false, suggestedLabel: "", checking: false, checked: true });
            this.props.isValid("", false);
        }
    };

    doAutocomplete(payload) {
        const searchID = uuidv4();
        this.setState({ searchID });
        this.cancellable = makeCancelable(GS.autocomplete(payload, searchID));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
    }

    render() {

        const { lang } = this.props;
        const { suggestedLabel, isDuplicate, isErrored, isLocalDuplicate } = this.state;

        if (isErrored) return <Errored data={isErrored} />;

        return (
            <React.Fragment>
                <TitledTextField
                    dataTest="language-value"
                    name="suggestedLabel"
                    title={"Label"}
                    required={true}
                    text={suggestedLabel}
                    placeholder={"Enter suggested label"}
                    margin={true}
                    maxLength={MAX_LABEL_LENGTH}
                    lang={lang}
                    error={isDuplicate}
                    errorText={isDuplicate ?
                        isLocalDuplicate ?
                            "This value already exists on this concept as a different label."
                            :
                            "This value already exists on another concept as a preferred label."
                        :
                        null
                    }
                    onChange={this.handleChange} />
            </React.Fragment>
        );
    }
}

SuggestionPrefLabelField.propTypes = {
    graph: PropTypes.string.isRequired,
    isValid: PropTypes.func.isRequired,
    schemeIRI: PropTypes.string,
    optionForXL: PropTypes.bool.isRequired,
    existingValues: PropTypes.object,
    name: PropTypes.string,
    lang: PropTypes.string.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(withGS(SuggestionPrefLabelField, ["ALL"], null, {}));